import { Avatar } from '@mantine/core';
import { IconChecks } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { trackHomeCardLinkClicked } from 'src/Services/Mixpanel/events';
import { DashboardTaskDto } from 'src/types/dashboard.types';

import { CardItem, TwoColumnCard } from '../TwoColumnCard';

interface Props {
  tasks: DashboardTaskDto[];
}

const firstNameOf = (assignedToName: string | null | undefined) =>
  (assignedToName ?? '').trim().split(' ')[0] ?? '';

const initialsOf = (assignedToName: string | null | undefined) => {
  const parts = (assignedToName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + last).toUpperCase();
};

const AssigneeAvatar = ({ task }: { task: DashboardTaskDto }) => (
  <Avatar
    src={task.assignedToPictureUrl ?? undefined}
    name={initialsOf(task.assignedToName)}
    color="initials"
    size={28}
  />
);

// Same overdue rule as the header TaskBadge (dueDate strictly before start of today).
const isOverdueTask = (task: DashboardTaskDto) => {
  if (typeof task.isOverdue === 'boolean') return task.isOverdue;
  if (!task.dueDate) return false;
  return dayjs(task.dueDate).isBefore(dayjs().startOf('day'));
};

const sortUpcoming = (a: DashboardTaskDto, b: DashboardTaskDto) => {
  // Tasks without a due date go last.
  if (!a.dueDate && !b.dueDate) return 0;
  if (!a.dueDate) return 1;
  if (!b.dueDate) return -1;
  return dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf();
};

const TasksCard = ({ tasks }: Props) => {
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();

  const overdue = tasks.filter(isOverdueTask);
  const upcoming = [...tasks].filter(task => !isOverdueTask(task)).sort(sortUpcoming);

  const daysLate = (dueDate: string) => dayjs().startOf('day').diff(dayjs(dueDate), 'day');

  const buildUpcomingItem = (task: DashboardTaskDto) => (
    <CardItem
      key={task.id}
      leading={<AssigneeAvatar task={task} />}
      title={task.title}
      subtitle={firstNameOf(task.assignedToName)}
      trailing={task.dueDate ? dayjs(task.dueDate).format('ddd DD') : '—'}
    />
  );

  const buildOverdueItem = (task: DashboardTaskDto) => (
    <CardItem
      key={task.id}
      leading={<AssigneeAvatar task={task} />}
      title={task.title}
      subtitle={firstNameOf(task.assignedToName)}
      trailing={
        task.dueDate
          ? t('dashboard.cards.tasks.since', 'depuis {{count}} j', {
              count: daysLate(task.dueDate),
            })
          : undefined
      }
      emphasizeTrailing
    />
  );

  return (
    <TwoColumnCard
      title={t('dashboard.cards.tasks.title', 'Les tâches')}
      linkLabel={t('dashboard.cards.tasks.link', 'Voir toutes les tâches')}
      linkTo={`/asset/${assetId}/tasks`}
      onLinkClick={() => trackHomeCardLinkClicked({ card: 'tasks' })}
      accent="sauge"
      icon={<IconChecks size={20} stroke={1.75} />}
      left={{
        title: t('dashboard.cards.tasks.upcoming', 'Prochaines'),
        count: upcoming.length,
        items: upcoming.map(buildUpcomingItem),
        empty: t('dashboard.cards.tasks.upcomingEmpty', 'Rien à venir'),
      }}
      right={{
        title: t('dashboard.cards.tasks.overdue', 'En retard'),
        count: overdue.length,
        tone: 'late',
        items: overdue.map(buildOverdueItem),
        empty: t('dashboard.cards.tasks.overdueEmpty', 'Aucun retard'),
      }}
    />
  );
};

export default TasksCard;
